import { useEffect, useCallback } from "react";
import { IncomeDocumentationSection } from "@/features/applicant/components/IncomeDocumentationSection";

interface BankDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (bankName: string, fileOrFiles: File | File[]) => Promise<void>;
  isSubmitting?: boolean;
  existingBankName?: string;
  maxSizeMb?: number;
  existingFileCount?: number;
  existingFileNames?: string[];
}

export function BankDetailsModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  existingBankName,
  maxSizeMb = 5,
  existingFileCount = 0,
  existingFileNames = [],
}: BankDetailsModalProps) {
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bank-modal-title"
    >
      <div
  style={{
    backgroundColor: "#fff",
    borderRadius: "0.5rem",
    boxShadow:
      "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    width: "100%",
    maxWidth: "1260px",
    maxHeight: "90vh",
    overflowY: "auto",
    padding: "16px",
    paddingBottom: "env(safe-area-inset-bottom)",
  }}
  onClick={(e) => e.stopPropagation()}
>
  <div className="p-6 border-b border-gray-200">
    {/* Centered Title */}
    <h2
      id="bank-modal-title"
      className="text-xl font-semibold text-blue-900 text-center"
    >
      Supporting Documents
    </h2>

    {/* Original description */}
    <p className="mt-2 text-sm text-gray-600">
      Please upload bank statements and/or pay stubs at this time.
      Failure to provide these documents may delay your application.
    </p>

    {/* Two Bullet Points */}
    <ul className="mt-3 p-3 text-sm text-gray-600 list-disc list-inside space-y-2">
      <li>
      You will need to upload bank statements that include individual
      deposits for all listed income sources for the last three months.
      Please provide for every account that receives income from the sources provided on your application.
      </li>
      <li>
        You will also need to provide documents that can prove your income
        source, including pay stubs, award letters (e.g. for VA or
        disability benefits), or spousal support documents.
      </li>
       <li>
       You may upload up to six documents at this time.
      </li>
    </ul>
  </div>

  <div className="p-6 space-y-4">
    <IncomeDocumentationSection
      omitOuterCard
      omitDescriptor
      hideFileList
      showDropzone
      onUploadFiles={async (files) => {
        await onSubmit(
          existingBankName || "",
          files.length === 1 ? files[0] : files,
        );
      }}
      existingUploadCount={existingFileCount}
      existingUploadNames={existingFileNames}
      maxSizeMb={maxSizeMb}
      uploading={isSubmitting}
      message={null}
      isAwaiting={false}
      isReadOnly={false}
      proofOfIncome={[]}
      onPreview={() => {}}
      onRemove={async () => {}}
    />
  </div>
</div>
    </div>
  );
}
